/** A cave map being drawn by the generator: legend characters plus a mask of cells already spoken for. */

import type { Rng } from '../../sim/rng';

export interface Rect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

export interface Cell {
  readonly x: number;
  readonly y: number;
}

export class Sketch {
  readonly w: number;
  readonly h: number;
  /** One legend character per cell, row by row. */
  readonly cells: string[];
  /** 1 where a feature, the inbox's pocket or the exit's doorstep already lies: later features keep out. */
  readonly used: Uint8Array;

  constructor(w: number, h: number) {
    this.w = w;
    this.h = h;
    this.cells = [];
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) this.cells.push(this.onBorder(x, y) ? 'W' : '.');
    }
    this.used = new Uint8Array(w * h);
  }

  onBorder(x: number, y: number): boolean {
    return x === 0 || y === 0 || x === this.w - 1 || y === this.h - 1;
  }

  /** Inside the steel border. */
  inside(x: number, y: number): boolean {
    return x > 0 && y > 0 && x < this.w - 1 && y < this.h - 1;
  }

  get(x: number, y: number): string {
    if (x < 0 || y < 0 || x >= this.w || y >= this.h) return 'W';
    return this.cells[y * this.w + x] ?? 'W';
  }

  /** Writes inside the border only: the border stays steel whatever a feature asks. */
  set(x: number, y: number, ch: string): void {
    if (this.inside(x, y)) this.cells[y * this.w + x] = ch;
  }

  isUsed(x: number, y: number): boolean {
    return !this.inside(x, y) || this.used[y * this.w + x] === 1;
  }

  /** The rect lies inside the border and touches nothing claimed. */
  free(r: Rect): boolean {
    if (r.x < 1 || r.y < 1 || r.x + r.w > this.w - 1 || r.y + r.h > this.h - 1) return false;
    for (let y = r.y; y < r.y + r.h; y++) {
      for (let x = r.x; x < r.x + r.w; x++) if (this.used[y * this.w + x] === 1) return false;
    }
    return true;
  }

  /** The part of the rect inside the border (it may come out empty). */
  clip(r: Rect): Rect {
    const x = Math.max(1, r.x);
    const y = Math.max(1, r.y);
    const w = Math.max(0, Math.min(this.w - 1, r.x + r.w) - x);
    return { x, y, w, h: Math.max(0, Math.min(this.h - 1, r.y + r.h) - y) };
  }

  /** Marks the part of the rect inside the border as claimed. */
  claim(r: Rect): void {
    for (let y = r.y; y < r.y + r.h; y++) {
      for (let x = r.x; x < r.x + r.w; x++) if (this.inside(x, y)) this.used[y * this.w + x] = 1;
    }
  }

  /** Every cell of the rect set to `ch`. */
  fillRect(r: Rect, ch: string): void {
    for (let y = r.y; y < r.y + r.h; y++) for (let x = r.x; x < r.x + r.w; x++) this.set(x, y, ch);
  }

  /** The rect's outline set to `ch`. */
  outline(r: Rect, ch: string): void {
    for (let x = r.x; x < r.x + r.w; x++) {
      this.set(x, r.y, ch);
      this.set(x, r.y + r.h - 1, ch);
    }
    for (let y = r.y; y < r.y + r.h; y++) {
      this.set(r.x, y, ch);
      this.set(r.x + r.w - 1, y, ch);
    }
  }

  /**
   * A random free spot for a `w` x `h` rect, or undefined after `tries` misses. `ok` may veto a
   * spot (a creature too near the inbox, an amoeba too near a creature).
   */
  findSpot(rng: Rng, w: number, h: number, ok?: (r: Rect) => boolean, tries = 40): Rect | undefined {
    const spanX = this.w - 2 - w + 1;
    const spanY = this.h - 2 - h + 1;
    if (spanX < 1 || spanY < 1) return undefined;
    for (let i = 0; i < tries; i++) {
      const r = { x: 1 + rng.int(spanX), y: 1 + rng.int(spanY), w, h };
      if (this.free(r) && (ok === undefined || ok(r))) return r;
    }
    return undefined;
  }

  /** A random unclaimed interior cell holding `ch`, or undefined. */
  findCell(rng: Rng, ch: string, ok?: (c: Cell) => boolean, tries = 200): Cell | undefined {
    for (let i = 0; i < tries; i++) {
      const c = { x: 1 + rng.int(this.w - 2), y: 1 + rng.int(this.h - 2) };
      if (!this.isUsed(c.x, c.y) && this.get(c.x, c.y) === ch && (ok === undefined || ok(c))) return c;
    }
    return undefined;
  }

  rows(): string[] {
    const out: string[] = [];
    for (let y = 0; y < this.h; y++) out.push(this.cells.slice(y * this.w, (y + 1) * this.w).join(''));
    return out;
  }
}

/** Chebyshev distance: how many king moves apart. */
export function kingDistance(a: Cell, b: Cell): number {
  return Math.max(Math.abs(a.x - b.x), Math.abs(a.y - b.y));
}

/** Chebyshev distance from a cell to the nearest cell of a rect (0 inside it). */
export function rectDistance(r: Rect, c: Cell): number {
  const dx = c.x < r.x ? r.x - c.x : c.x >= r.x + r.w ? c.x - (r.x + r.w - 1) : 0;
  const dy = c.y < r.y ? r.y - c.y : c.y >= r.y + r.h ? c.y - (r.y + r.h - 1) : 0;
  return Math.max(dx, dy);
}
